import { memo, ReactNode } from 'react';
import { Avatar, Box, Flex, Text, Tooltip } from '@chakra-ui/react';
import { iconSize } from '@duke-hq/ui/constants/scalable';

// Width of the avatar column, shared by the avatar, the carry-over circle and
// the empty spacer so a run of messages lines up regardless of which slot shows.
const AVATAR_COLUMN = '2rem';

// Hoisted so the avatar's hover style keeps a stable identity across renders
// (a fresh object each render would defeat the surrounding memo).
const AVATAR_HOVER_STYLE = { opacity: 0.65 };

export interface ConversationMessagePersona {
  name: string;
  avatarUrl: string | null;
  /**
   * Optional avatar background colour. When omitted the avatar uses Chakra's
   * auto colour from the name; hosts that have a configured persona colour can
   * pass it so the avatar matches elsewhere.
   */
  avatarBg?: string;
}

export interface ConversationMessageProps {
  persona: ConversationMessagePersona;
  /** Render the persona name above the bubble. */
  showName: boolean;
  /** Render the persona avatar beside the bubble (a placeholder holds the space otherwise). */
  showAvatar: boolean;
  /**
   * When the avatar slot is empty (`showAvatar` false), show an empty dashed
   * circle in place of the blank spacer to signal this message keeps the same
   * persona as the previous one (the persona carries over), rather than leaving
   * a gap. Editable hosts pass this so a continuing message still exposes a
   * change-persona affordance; read-only hosts may pass it for the visual.
   */
  continuesPersona?: boolean;
  /**
   * Wraps the persona slot (the avatar or the carry-over circle) so an editable
   * host can make it open a persona picker, e.g. by returning the slot inside a
   * menu trigger. Receives the rendered slot element. Omitted in read-only hosts
   * (student runtime, previews), where the slot is plain and non-interactive.
   */
  wrapPersona?: (slot: ReactNode) => ReactNode;
  /**
   * Optional affordances rendered just after the bubble, on its trailing edge
   * (e.g. the flow editor's persona/delete menu). Positioned out of the layout
   * flow so they never change the bubble's width. Hosts own their visibility.
   */
  actions?: ReactNode;
  /**
   * Optional affordance rendered just before the bubble, on its leading edge
   * (e.g. the flow editor's reorder grip). Out of the layout flow like
   * `actions`. Hosts own their visibility.
   */
  leading?: ReactNode;
  isRtl?: boolean;
  children: ReactNode;
}

// -- ConversationMessage -------------------------------------------------------
// The single source of truth for how one conversation turn is rendered, used by
// the student runtime and the admin previews so they can't drift. Owns its own
// alignment, avatar column, bubble and font baseline so it renders the same in
// any host, with no reliance on container CSS.
function ConversationMessageComponent({
  persona,
  showName,
  showAvatar,
  continuesPersona,
  wrapPersona,
  actions,
  leading,
  isRtl,
  children,
}: ConversationMessageProps) {
  const interactive = wrapPersona !== undefined;
  const wrap = (slot: ReactNode) => (wrapPersona ? wrapPersona(slot) : slot);
  return (
    <Flex
      direction="column"
      align="flex-start"
      width="100%"
      fontSize="md"
      dir={isRtl ? 'rtl' : undefined}
    >
      {showName ? (
        <Text
          paddingInlineStart={`calc(${AVATAR_COLUMN} + var(--chakra-space-2))`}
          pb="1"
          pt="2"
          fontSize="sm"
          fontWeight="bold"
          color="fg.muted"
          // Keeps the caret out of the name when the message is hosted inside
          // an editable surface (the flow editor's node views). Inert in
          // read-only hosts.
          contentEditable={false}
        >
          {persona.name}
        </Text>
      ) : null}
      {/* Cap the bubble to match ConversationReplyBubble so messages (text and
          media) hug their content instead of stretching across the full width. */}
      <Flex align="flex-end" maxW={{ base: '75%', md: '70%' }}>
        {showAvatar ? (
          wrap(
            <Avatar
              boxSize={AVATAR_COLUMN}
              name={persona.name}
              size={iconSize.xs}
              color="white"
              bg={persona.avatarBg}
              src={persona.avatarUrl ?? undefined}
              flexShrink={0}
              cursor={interactive ? 'pointer' : undefined}
            />,
          )
        ) : continuesPersona ? (
          // A muted (semi-transparent) avatar in the same column and at the same
          // diameter as a solid avatar so the run lines up. The reduced opacity
          // signals the persona is carried over from the previous message rather
          // than set explicitly here; the muted avatar still shows who is
          // speaking. Clicking opens the picker.
          <Tooltip label="Change persona" placement="bottom" isDisabled={!interactive}>
            {wrap(
              <Avatar
                boxSize={AVATAR_COLUMN}
                name={persona.name}
                size={iconSize.xs}
                color="white"
                bg={persona.avatarBg}
                src={persona.avatarUrl ?? undefined}
                flexShrink={0}
                opacity={0.45}
                cursor={interactive ? 'pointer' : undefined}
                _hover={interactive ? AVATAR_HOVER_STYLE : undefined}
                aria-label={interactive ? 'Change persona' : undefined}
              />,
            )}
          </Tooltip>
        ) : (
          <Box minW={AVATAR_COLUMN} flexShrink={0} />
        )}
        {/* The bubble appearance is owned here, independent of any app-level
            `.content` CSS. The inner content element is reset to avoid a double
            bubble. */}
        <Box
          marginInlineStart="2"
          w="fit-content"
          // Allow the bubble to shrink below a fixed-width child (e.g. the
          // YouTube player) so wide embeds clamp to the row instead of
          // overflowing. Intrinsic content like images is unaffected.
          minW={0}
          pos="relative"
          borderTopLeftRadius="2xl"
          borderTopRightRadius="2xl"
          borderBottomRightRadius="2xl"
          borderBottomLeftRadius="0"
          bg="bg.surface"
          py="1"
          px="3"
          sx={{
            '& .content': {
              borderRadius: 0,
              background: 'transparent',
              border: 'none',
              padding: 0,
            },
            '& .content p': { width: 'auto' },
          }}
        >
          {children}
          {/* The reorder grip overlaps the bubble's own left edge, so it's
              within the bubble's hover area (no gap to cross to reach it) and
              never competes with the avatar column for space. */}
          {leading ? (
            <Flex
              pos="absolute"
              insetInlineEnd="100%"
              top="50%"
              transform="translateY(-50%)"
              marginInlineEnd="-2"
              align="center"
            >
              {leading}
            </Flex>
          ) : null}
          {actions ? (
            <Flex
              pos="absolute"
              insetInlineStart="100%"
              top="50%"
              transform="translateY(-50%)"
              align="center"
              marginInlineStart="2"
            >
              {actions}
            </Flex>
          ) : null}
        </Box>
      </Flex>
    </Flex>
  );
}

// Memoised so a parent re-render alone (e.g. a sibling node updating) does not
// re-render every message. Effective where the host passes stable prop
// identities, such as the read-only static card and the student runtime.
export const ConversationMessage = memo(ConversationMessageComponent);
ConversationMessage.displayName = 'ConversationMessage';
